import moment from 'moment'
import i18n from '@/i18n'
import ListTreeViewComponent from '@/components/commons/list/tree-view/ListTreeViewComponent.vue'
import SelectorDialog from '@/components/commons/selector/dialog/SelectorDialog.vue'
import { TimeRanges, CustomTimeRangeTypes } from '@colven/common-domain-lib/lib'
import { getIds } from '@/tools/functions'
import { clone } from 'ramda'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

// componentes custom
import CommandsFileInputComponent from '@/components/command/massive-upload/file-input/CommandsFileInputComponent.vue'

export default {
  name: 'SelectorComponent',
  model: {
    prop: 'model',
    event: 'change'
  },
  /**
   * MODELO
   *
   * showClose: boolean, para mostrar/ocultar el botón de cerrar.
   * filters: arreglo de filtros (cada uno abre un diálogo para seleccionar).
   *  --> Ejemplo:
   *      filters: [
   *        {
   *          id: <identificador>,
   *          name: <nombre de la entidad que se quiere filtrar - ej: Camiones>,
   *          show: <boolean para mostrarlo/ocultarlo>,
   *          disabled: <boolean para deshabilitarlo>,
   *          data: <array de items que pueden ser seleccionados (es la totalidad del universo de datos, incluidos los ya seleccionados)>,
   *          selectedData: <array de items que ya fueron seleccionados>
   *          selectorDialogItems: <array de los items seleccionados en el diálogo>
   *        }
   *      ]
   * selects: arreglo de selectores del tipo dropdown (múltiples o simples).
   *  --> Ejemplo:
   *      selects: [
   *        {
   *          id: <identificador>,
   *          name: <nombre de lo que se quiere seleccionar - ej: Actividad>,
   *          show: <boolean para mostrarlo/ocultarlo>,
   *          disabled: <boolean para deshabilitarlo>,
   *          data: <array de items que pueden ser seleccionados>,
   *          selectedData: <array de items, o item, seleccionados>
   *          multiple: <boolean para habilitar la selección múltiple>
   *        }
   *      ]
   * autoReport: booleano para activar/desactivar el autoreporte,
   * autoReportMinutes: minutos del autoreporte,
   * disableSaveButton: boolean, para deshabilitar/habilitar el botón para generar el reporte.
   * showDateAndTimeSelector: boolean, para mostrar/ocultar el selector de rango de tiempo.
   * selectedDateAndTimeRange: el tipo de rango de tiempo seleccionado (constante TimeRanges).
   * selectedDateAndTimeRangeCustomType: el tipo de rango personalizado seleccionado (constante CustomTimeRangeTypes).
   * sinceDate: fecha en formato YYYY-MM-DD (null si no se inicializa).
   * sinceTime: hora en formato hh:mm (null si no se inicializa).
   * toDate: fecha en formato YYYY-MM-DD (null si no se inicializa con una fecha particular).
   * toTime: hora en formato hh:mm (null si no se inicializa).
   * customDateTimeValidForm: booleano que indica la validez del formulario de rangos de fecha y hora personalizado.
   *
   * EVENTOS
   *
   * change: el evento que se lanza cuando se modifica el modelo.
   * get-report: se lanza cuando se hace click en botón para obtener el reporte.
   * close: se lanza cuando se hace click en el botón para cerrar el selector.
   */
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  components: {
    SelectorDialog,
    ListTreeViewComponent,
    CommandsFileInputComponent,
    AutocompleteComponent
  },
  data () {
    return {
      dateAndTimeRanges: [
        // ultima hora, ultimas 6 horas, hoy, ayer, ultima semana
        {
          id: TimeRanges.LAST_HOUR,
          text: i18n.t('selector.dateAndTime.lastHour'),
          custom: false
        },
        {
          id: TimeRanges.LAST_SIX_HOUR,
          text: i18n.t('selector.dateAndTime.lastSixHours'),
          custom: false
        },
        {
          id: TimeRanges.TODAY,
          text: i18n.t('selector.dateAndTime.today'),
          custom: false
        },
        {
          id: TimeRanges.YESTERDAY,
          text: i18n.t('selector.dateAndTime.yesterday'),
          custom: false
        },
        {
          id: TimeRanges.LAST_WEEK,
          text: i18n.t('selector.dateAndTime.lastWeek'),
          custom: false
        },
        {
          id: TimeRanges.CUSTOM,
          text: i18n.t('selector.dateAndTime.custom'),
          custom: true
        }
      ],
      dateAndTimeCustomRangeTypes: [
        {
          text: i18n.t('selector.dateAndTime.customTypes.dateAndTime'),
          value: CustomTimeRangeTypes.DATE_AND_TIME
        },
        {
          text: i18n.t('selector.dateAndTime.customTypes.date'),
          value: CustomTimeRangeTypes.DATE
        }
      ],
      showDateAndTime: false,
      showAutoReport: false,
      menuSinceDate: false,
      menuToDate: false,
      menuSinceTime: false,
      menuToTime: false,
      rules: {
        required: value => !!value || `${i18n.t('required')}`,
        twoDatesValidation: (firstDate, secondDate) => firstDate.getTime() >= secondDate.getTime() || `${i18n.t('selector.dateAndTime.dateError')}`,
        twoTimesValidation: (firstTime, secondTime, firstDate, secondDate, selectedType) => (selectedType === CustomTimeRangeTypes.DATE) ||
        (selectedType === CustomTimeRangeTypes.DATE_AND_TIME && firstDate.getTime() < secondDate.getTime()) ||
        (selectedType === CustomTimeRangeTypes.DATE_AND_TIME && firstDate.getTime() === secondDate.getTime() && firstTime && secondTime && moment(firstTime, 'hh:mm').isSameOrBefore(moment(secondTime, 'hh:mm'), 'minute')) ||
        (selectedType === CustomTimeRangeTypes.TIME && firstTime && secondTime && moment(firstTime, 'hh:mm').isSameOrBefore(moment(secondTime, 'hh:mm'), 'minute')) ||
        `${i18n.t('selector.dateAndTime.timeError')}`
      },
      timeRanges: TimeRanges,
      customDateAndTimeRangeType: CustomTimeRangeTypes,
      selectorDialogModel: {
        normalizer: (node) => {
          return {
            id: node.id,
            label: node.name,
            children: node.children
          }
        },
        data: [],
        selected: null,
        show: false,
        title: i18n.t('selector.selector'),
        singleSelect: false,
        selectAll: false,
        saveDisabled: false
      },
      currentFilter: undefined
    }
  },
  computed: {
    autoReportSwitchLabel () {
      if (this.model.autoReport) {
        return this.$t('selector.autoReport.enabled')
      } else {
        return this.$t('selector.autoReport.disabled')
      }
    },
    disableAutoReport () {
      return !this.model.autoReport || this.model.selectedDateAndTimeRange === TimeRanges.YESTERDAY || this.model.selectedDateAndTimeRange === TimeRanges.CUSTOM
    },
    disableAutoReportSwitch () {
      return this.model.selectedDateAndTimeRange === TimeRanges.YESTERDAY || this.model.selectedDateAndTimeRange === TimeRanges.CUSTOM
    },
    formattedSinceDate () {
      return this.formatDate(this.model.sinceDate)
    },
    formattedToDate () {
      return this.formatDate(this.model.toDate)
    }
  },
  async mounted () {
    // Setea el lenguaje de los componentes de Vuetify
    this.$vuetify.lang.current = this.$i18n.locale
  },
  methods: {
    formatDate (date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    arrowIcon (show) {
      if (show) {
        return 'arrow_drop_up'
      } else {
        return 'arrow_drop_down'
      }
    },
    getIds (data, singleSelect) {
      if (singleSelect) {
        return data.length === 1 ? data[0].id : null
      } else {
        const result = []
        getIds(data, result)
        return result
      }
    },
    /**
     * Se carga el modelo de datos del diálogo de selección
     * @param {*} filterData
     */
    selectEnitites (filterData) {
      this.currentFilter = filterData
      this.selectorDialogModel.data = this.currentFilter.data
      /*
        Si no hay datos en la prop selectorDialogItems, lo cual significaría la primer apertura del diálogo
        para ese filtro, se verifica que no haya datos en la prop selectedData (si se configuraron unos datos
        seleccionados por defecto), o sino, se setea en null.
      */
      this.selectorDialogModel.selected = filterData.selectedData.length > 0 && this.currentFilter.selectorDialogItems
        ? this.currentFilter.selectorDialogItems : (filterData.selectedData.length > 0
          ? this.getIds(filterData.selectedData, filterData.singleSelect) : null)
      this.selectorDialogModel.title = this.$t('selector.selectionDialog.title', { entityName: filterData.name })
      this.selectorDialogModel.singleSelect = filterData.singleSelect
      this.selectorDialogModel.selectAll = false
      this.selectorDialogModel.show = true
    },
    cancelDialogAction () {
      this.selectorDialogModel.show = false
    },
    /**
     * TO DO: Implementar recursivamente (para n niveles)
     */
    saveDialogAction (data) {
      this.currentFilter.selectorDialogItems = clone(data)
      if (data) {
        const selectedItems = []
        if (this.currentFilter.singleSelect && data) {
          selectedItems.push(data)
        }
        const itemsCopy = clone(this.currentFilter.data)
        const filteredData = itemsCopy.filter(function filterFunction (item) {
          if (data.includes(item.id)) return true
          if (item.children && item.children.length > 0) {
            return (item.children = item.children.filter(filterFunction)).length
          }
        })
        selectedItems.cleanAndUpdate(filteredData)
        this.currentFilter.selectedData.cleanAndUpdate(selectedItems)
      } else {
        this.currentFilter.selectedData.clean()
      }
      if (this.currentFilter.selectAction) this.currentFilter.selectAction()
      this.selectorDialogModel.show = false
    },
    getReportClick () {
      this.$emit('save', {
        filters: this.model.filters,
        selects: this.model.selects,
        dateAndTimeRange: this.model.showDateAndTimeSelector ? this.model.selectedDateAndTimeRange : undefined,
        dateAndTimeRangeCustomType: this.model.showDateAndTimeSelector &&
          this.model.selectedDateAndTimeRange === TimeRanges.CUSTOM ? this.model.selectedDateAndTimeRangeCustomType : undefined,
        sinceDate: this.model.showDateAndTimeSelector ? this.model.sinceDate : undefined,
        sinceTime: this.model.showDateAndTimeSelector ? this.model.sinceTime : undefined,
        toDate: this.model.showDateAndTimeSelector ? this.model.toDate : undefined,
        toTime: this.model.showDateAndTimeSelector ? this.model.toTime : undefined,
        autoReport: this.model.autoReport,
        autoReportMinutes: this.model.autoReportMinutes,
        customInputComponentModel: this.model.customInputComponent.model
      })
    },
    sortData (select) {
      select.data.sort(function sortFunction (a, b) {
        const nameA = a[select.text].toLowerCase()
        const nameB = b[select.text].toLowerCase()
        if (nameA < nameB) {
          return -1
        } else if (nameA > nameB) {
          return 1
        } else {
          return 0
        }
      })
    },
    close () {
      this.$emit('close')
    },
    selectedFunction (newValue, select) {
      select.selectedData = newValue;
      if (!select.multiple) {
        select.selectedData = newValue && select && select.value ? newValue[select.value] : null;
      } else {
        select.selectedData = [];
        newValue.forEach(nv => {
          if (nv && select && select.value && nv[select.value]) {
            select.selectedData.push(nv[select.value]);
          }
        })
      }
    },
  },
  watch: {
    'model.selectedDateAndTimeRange': function (val) {
      if (this.model.autoReport && (val === TimeRanges.YESTERDAY || val === TimeRanges.CUSTOM)) {
        this.model.autoReport = false
      }
    }
  }
}
